import { indexToLetter } from "helpers/utils";
import type { LexicalCommand, LexicalNode, ParagraphNode } from "lexical";
import { ElementNode, RootNode } from "lexical";
import { $isElementNode } from "lexical";

import type { ArticleContainerNode } from "./ArticlePlugin/ArticleContainerNode";
import type { ArticleContentNode } from "./ArticlePlugin/ArticleContentNode";
import type { ConditionalNode } from "./ConditionalNode";
import type { ConditionalContentNode } from "./ConditionalPlugin/ConditionalContentNode";
import type { RepeatableContentNode } from "./RepeatablePlugin/RepeatableContentNode";

export type TBlockAttribute = {
    nodeType: "conditional-content" | "repeatable-content";
    nodeParent: ConditionalContentNode | ConditionalNode | RepeatableContentNode | null;
    insertAction: (node: ParagraphNode | LexicalNode[]) => LexicalCommand<ParagraphNode | LexicalNode[]> | boolean;
};

export type TLexicalBlockType = ConditionalContentNode | ConditionalNode | RepeatableContentNode;

/**
 * Get index of variable key ex : beneficiaries.0.name -> 0
 * @param variableKey multi-signature variable key
 */
export const getBeneficiaryIndex = (variableKey: string) => {
    const regex = /beneficiaries\.(\d+)\./;
    const match = variableKey.match(regex);
    if (match) {
        return parseInt(match[1], 10);
    }
};

/**
 * This function is unsed in recalculateNuberingArticles()
 * it transform numbering [1] for Article 1., [1, 2] for Article 1.2. into correspondant letter a) b) c) ...
 * @param { number[] } numbering
 */
export const getLetterNumbering = (numbering: number[]) => {
    const lastNumber = numbering[numbering.length - 1];
    return indexToLetter(lastNumber - 1).toLowerCase();
};

/**
 * Check if article title has letter numbering a) b) c) ...
 * @param articleTitle Article Title
 */
export const hasLetterNumbering = (articleTitle: string) => {
    const regex = /^\s*[a-zA-Z]\)\s*/;
    return regex.test(articleTitle);
};

/**
 * Isolate first part numbering from an Article title ex :
 * Input : "Article 1. Anohjr's coding"  -> expected Output : "1."
 * Input : "1.2.3 Anohjr's coding"  -> expected Output : "1.2.3."
 * Input : "a) Anohjr's coding"  -> expected Output : "a)"
 *
 * And it handle Optionnal cases ->
 * Input : "(Optional) Article 1. Anohjr's coding"  -> expected Output : "1."
 * Same for : 1.2.3, a), b), c) cases ...
 */

export const getArticleTitleNumbering = (articleTitle: string) => {
    const articleNumberingRegex = /^(?:\(\w+\)\s*)?(Article\s+)?((\d+(\.\d+)*\.)|([a-z])\))/;
    return articleTitle?.match(articleNumberingRegex)?.[2];
};

/**
 * Logic to get Reference article node. Needs to be formatted for a) b) c) nodes.
 * Reference should be like : Article 1.a or Article 1.2.a ..
 */
export const getArticleReference = (articleNode: ArticleContainerNode) => {
    const parentArticle = articleNode.getParent<ArticleContentNode>()?.getParent<ArticleContainerNode>();
    const articleTitle = articleNode.getFirstChild()?.getTextContent();
    const numbering = getArticleTitleNumbering(articleTitle ?? "");
    const isLetterNumbering = hasLetterNumbering(numbering ?? "");

    if (isLetterNumbering) {
        const parentArticleTitle = parentArticle?.getFirstChild()?.getTextContent();
        const parentArticleNumbering = getArticleTitleNumbering(parentArticleTitle ?? "");
        return `Article ${parentArticleNumbering}${numbering?.replace(")", "")}`; // Replace parenthesis ex : Article 1.a) -> Article 1.a
    } else {
        return `Article ${numbering}`;
    }
};
